import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ErrorLine } from '../../components/Lines';
import {
  checkPasswords,
  NO_PASSWORDS,
  PasswordFields,
  type PasswordValues,
} from '../../components/PasswordFields';
import { errorText } from '../../errors';
import { t } from '../../i18n';
import { useMember, useSession } from '../../session';

/**
 * What Profile's link leaves in this screen's history entry: the page load it was opened in. A
 * reload keeps the history's entries but not the app's memory, so the mark counts only in its own
 * load, where the entry before this one is that Profile and the session's memory still holds.
 */
export type FromProfileState = { fromProfile: number };

/** This page load's mark: when the app started, which a reload makes afresh. */
export const FROM_PROFILE: FromProfileState = { fromProfile: Date.now() };

function openedFromProfile(state: unknown): boolean {
  return (state as Partial<FromProfileState> | null)?.fromProfile === FROM_PROFILE.fromProfile;
}

/**
 * A member's own change of password (A6; D21, D26), at /profile/password, admins' too (D23): the
 * current password, always, and the new one twice, checked on the phone first as the forced change
 * does (PasswordFields; D22). The server then ends every other session of theirs and starts a new
 * one here (D21), which the screen says first; once it's saved, Profile says so.
 */
export function ProfilePassword() {
  const member = useMember();
  const { changePassword, notePasswordChanged } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [values, setValues] = useState<PasswordValues>(NO_PASSWORDS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const repeatRef = useRef<HTMLInputElement>(null);
  // Whether this form is still the page shown: the change's answer can come after the member has
  // left it (Back, a swipe, a tab), and their own move then stands (D29).
  const shown = useRef(false);

  useEffect(() => {
    shown.current = true;
    return () => {
      shown.current = false;
    };
  }, []);

  // Leaving goes to Profile with Profile once in the history, so Back from it goes straight back
  // (D28). Opened from Profile in this page load, the entry before this one is that Profile: back
  // one entry. Opened any other way (the admin's own page in Members, its address, or before a
  // reload, after which the entry before may load the app afresh, without the note), Profile takes
  // this screen's entry, as before.
  const backToProfile = openedFromProfile(location.state);
  const toProfile = () => (backToProfile ? navigate(-1) : navigate('/profile', { replace: true }));

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const checked = checkPasswords(values, true);
    if (checked.kind === 'empty') {
      const refs = { current: currentRef, next: nextRef, repeat: repeatRef };
      refs[checked.field].current?.focus();
      return;
    }
    if (checked.kind === 'error') {
      setError(t(checked.code));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      // The session takes the answer's member; the browser, the cookie of the session started here.
      await changePassword(values.current, values.next);
      // Left while it saved: wherever the member went stays, and the note goes unsaid (D29).
      if (!shown.current) return;
      // Profile says so once, from the session's memory: going back can't write its entry.
      notePasswordChanged();
      toProfile();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <section className="screen screen-narrow">
      <h1>{t('profilePassword.title')}</h1>
      <p className="muted">{t('profilePassword.intro')}</p>
      <form className="form" onSubmit={onSubmit} noValidate>
        <PasswordFields
          username={member.username}
          askCurrent
          values={values}
          onChange={setValues}
          currentRef={currentRef}
          nextRef={nextRef}
          repeatRef={repeatRef}
        />
        <ErrorLine text={error} />
        <button type="submit" className="button" disabled={busy}>
          {busy ? t('password.submitting') : t('password.submit')}
        </button>
      </form>
      {/* Not while saving: the change would be made all the same, unsaid. */}
      <button
        type="button"
        className="button button-quiet"
        onClick={() => toProfile()}
        disabled={busy}
      >
        {t('app.cancel')}
      </button>
    </section>
  );
}
